import type { FC } from 'react';
import React, { useCallback, useEffect } from 'react';

import { getCloudId } from '@helpcenter/meta';
import { di } from 'react-magnetic-di';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import type { ConnectionProviders } from '../../common/types';
import { useConnectionStatus } from '../../controllers/store';
import { ThirdPartyCardConnector } from './card-connectors';
import { ThirdPartyInlineConnector } from './inline-connectors';
import type { ConnectorProp } from './types';
import { ThirdPartyVsaBannerConnector } from './vsa-banner-connectors';

export const ThirdPartyConnectorRenderer: FC<ConnectorProp> = ({ connectors, type }) => {
    di(useConnectionStatus);
    const [{ isFetched, data: connectionStatus }, { fetchConnectionStatus }] = useConnectionStatus();
    const allowedConnectors = Object.keys(connectionStatus).filter(
        (provider: ConnectionProviders) => connectionStatus[provider] && !connectionStatus[provider]?.isUserOAuthed
    ) as typeof connectors;
    const cloudId = getCloudId();

    useEffect(() => {
        if (!isFetched) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            fetchConnectionStatus(cloudId);
        }
    }, [cloudId, fetchConnectionStatus, isFetched]);

    const renderConnectors = useCallback(() => {
        return Array.from(new Set(allowedConnectors))
            .slice(0, 1)
            .map((provider) => {
                const key = `${type}_${provider}`;
                const CONNECTOR_COMPONENTS = {
                    banner: <ThirdPartyVsaBannerConnector key={key} provider={provider} />,
                    card: <ThirdPartyCardConnector provider={provider} key={key} />,
                    inline: <ThirdPartyInlineConnector provider={provider} key={key} />,
                };
                return CONNECTOR_COMPONENTS[type];
            });
    }, [allowedConnectors, type]);

    return renderConnectors();
};

const ThirdPartyConnectorWithErrorBoundary = (props: ConnectorProp) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    return (
        <ErrorBoundary
            id="third-party-card-connector"
            packageName="third-party-connectors"
            onError={(err) =>
                fireTrackAnalytics(
                    createAnalyticsEvent({
                        attributes: {
                            errorMessage: err?.message,
                        },
                    }),
                    'thirdPartyConnectorsExperience failed'
                )
            }
        >
            <ThirdPartyConnectorRenderer {...props} />
        </ErrorBoundary>
    );
};

export default ThirdPartyConnectorWithErrorBoundary;
